import { ALL_MODULE_BOUNDARY_PATHS } from './billing';
import {
  isComplianceTrialModuleSet,
  isComplianceTrialRouteAllowed,
} from './compliance-trial-access';

export const PLAN_EXEMPT_EXACT_PATHS = new Set([
  '/',
  '/overview',
  '/translate',
  '/me/profile',
  '/me/credentials',
  // Self-service clinical-PIN enrolment — an identity credential (like
  // /me/credentials), not a plan feature. Without this exemption an RN/EN in a
  // normal tenant would hit the module-gate before they could set the PIN that
  // authenticates them as an S8 witness on the web MAR.
  '/me/clinical-pin',
  '/onboarding',
  // The admin hub chooses adoption; individual connection cards retain plan entitlements.
  '/settings/integrations',
]);

export const PLAN_EXEMPT_PREFIXES = [
  '/api',
  '/auth',
  '/_next',
  '/login',
  '/signup',
  '/access',
  '/admin/system',
  '/help',
  '/tasks',
  '/workflows/instances',
] as const;

export const SUBSCRIPTION_BLOCKED_EXACT_PATHS = new Set([
  '/',
  '/overview',
  '/settings',
  '/settings/billing',
  '/help',
  '/me/profile',
  '/access/subscription-inactive',
]);

export const SUBSCRIPTION_BLOCKED_PREFIXES = [
  '/api',
  '/auth',
  '/_next',
  '/login',
  '/signup',
  '/access',
  '/settings/billing',
  '/help',
] as const;

export const MODULE_LANDING_PATHS = new Set([
  '/compliance',
  '/hr',
  '/home-care',
  '/dashboards',
  '/residential',
  '/workforce',
  // R1 BLOCKER (2026-09-21): an Essentials tenant's prudential capability
  // grant (billing.ts COMPLIANCE_CAPABILITY_DEFINITIONS['/compliance/
  // prudential']) is an explicit child allow-list — deposits, governance,
  // reports, liquidity, permitted-uses — that deliberately does NOT include
  // the bare '/compliance/prudential' hub, because a literal grant of a
  // parent path also grants every descendant via segment-prefix matching
  // (modulePatternMatches below), which would silently re-open /pricing and
  // /accommodation-agreements. Adding the hub here instead makes it
  // reachable ONLY when the tenant already has at least one of its granted
  // children (financial_management tenants already have the bare parent
  // directly, so this is a no-op for them), without that reachability
  // cascading to any UNLISTED child — hasLandingModuleAccess only fires when
  // the pathname EXACTLY equals a landing path, never for a deeper path like
  // /compliance/prudential/pricing.
  '/compliance/prudential',
  // Deliberately does NOT include '/people': that bare route renders the
  // full worker administration table (loadPeopleWorkforceOverview), the same
  // content class as /people/workers, not a lightweight navigation hub like
  // /compliance. Granting it as a landing path would hand a
  // compliance_essentials-only (Essentials) tenant exactly the full worker
  // workspace the Wave 5 design excludes. Essentials tenants reach
  // /people/compliance directly by its own module grant, and (Wave 5 slice 4,
  // 2026-09-22) exactly the /people/policies-conduct/governance and /policies
  // children — never /people/learning or the bare /people/policies-conduct
  // hub, both deferred until a Wave 6 lite staff register exists; see
  // ESSENTIALS_CAPABILITIES_DEFERRED_UNTIL_LITE_REGISTERS in billing.ts.
  // nav-v2 must link to those leaves,
  // not to the bare /people hub, for an Essentials tenant.
]);

const PARENT_LAYOUT_ONLY_MODULE_PATHS = new Set(['/payroll']);

type ModuleAccessAlias = {
  routePrefix: string;
  modulePath: string;
  preserveSuffix?: boolean;
};

export function isClinicalWoundsCompatPath(pathname: string): boolean {
  return pathname === '/clinical/wounds' || pathname.startsWith('/clinical/wounds/');
}

const MODULE_ACCESS_ALIASES: ReadonlyArray<ModuleAccessAlias> = [
  // Existing RAC accommodation entitlement; no new module or billing package.
  { routePrefix: '/residential/evacuation-list', modulePath: '/residential/rooms' },
  // Recovery shares the leaf guard's incident/S8/restrictive-practice entitlements.
  { routePrefix: '/compliance/record-recovery', modulePath: '/compliance/incidents' },
  { routePrefix: '/compliance/record-recovery', modulePath: '/medications/s8-register' },
  { routePrefix: '/compliance/record-recovery', modulePath: '/compliance/restrictive-practices' },
  // A4: /wounds/:id bookmarks 307 to /clinical/wounds/:id. Tenants that
  // purchased /wounds without /clinical must still reach the detail leaf.
  { routePrefix: '/clinical/wounds', modulePath: '/wounds' },
  { routePrefix: '/me/work-readiness', modulePath: '/me/training' },
  // Wave 5 slice 1 (2026-09-21) briefly had an Essentials tenant carry
  // /me/compliance without /me/training — people-workforce-route-
  // convergence.test.ts pins exactly ONE consolidated "My Work Readiness"
  // nav entry rather than a separate worker page per underlying route, so
  // that single entry must stay reachable by whichever of its composed
  // capabilities the tenant actually has. Wave 5 slice 4 (2026-09-22)
  // deferred /me/compliance from Essentials; Paul re-enabled it on
  // 2026-10-01, so this alias now authorises the entry for Essentials plans
  // as well as plans carrying /me/compliance via workforce_operations. The
  // page itself handles a signed-in user without a linked `staff` row with
  // an empty state. This is entitlement aliasing only, not a page redirect.
  { routePrefix: '/me/work-readiness', modulePath: '/me/compliance' },
  // Worker administration has direct legacy boundary equivalents. Preserve
  // the nested leaf so narrowly purchased routes such as /staff/new continue
  // to authorize the canonical destination.
  { routePrefix: '/people/workers', modulePath: '/staff', preserveSuffix: true },
  { routePrefix: '/people/onboarding', modulePath: '/hr/onboarding' },
  // Native recruitment belongs to hiring/onboarding, not every People shell
  // entitlement. Keep this leaf ahead of the generic People-family aliases.
  { routePrefix: '/people/recruitment', modulePath: '/hr/onboarding' },
  { routePrefix: '/people/offboarding', modulePath: '/hr/offboarding' },
  { routePrefix: '/people/compliance/screening', modulePath: '/compliance/workforce' },
  { routePrefix: '/people/compliance/certifications', modulePath: '/compliance/workforce' },
  { routePrefix: '/people/compliance/obligations', modulePath: '/compliance/workforce' },
  { routePrefix: '/people/compliance', modulePath: '/compliance/workforce' },
  // Wave 5 slice 4 (2026-09-22, adversarial review item 4): the
  // /people/policies-conduct/code alias to /compliance/code-of-conduct was
  // removed. It resolved to a route Essentials tenants already carry (a
  // base compliance_essentials grant), which meant an Essentials tenant
  // reached PoliciesConductCodePage's own worker-scoped acknowledgment
  // tracking — a DIFFERENT, still-staff-dependent page from
  // /compliance/code-of-conduct itself — via alias even though /code is
  // deliberately deferred (see ESSENTIALS_CAPABILITIES_DEFERRED_UNTIL_
  // LITE_REGISTERS in billing.ts). Every non-Essentials tenant already
  // reaches /people/policies-conduct/code through the bare parent's own
  // direct workforce_operations grant, unaffected by this removal.
  {
    routePrefix: '/people/policies-conduct/governance',
    modulePath: '/compliance/policies',
  },
  {
    routePrefix: '/people/development/casual-conversion',
    modulePath: '/hr/casual-conversion',
  },
  {
    routePrefix: '/people/development/supervision',
    modulePath: '/clinical/supervision',
  },
  { routePrefix: '/hr/documents', modulePath: '/hr/onboarding' },
  // Learning is a canonical People destination, while billing entitlement
  // remains the provider's existing HR training module.
  { routePrefix: '/people/learning', modulePath: '/hr/training' },
  // Wave 5 slice 4 (2026-09-22, adversarial review item 4): the two generic
  // bare-prefix aliases that used to sit here —
  // `{ routePrefix: '/people/policies-conduct', modulePath: '/hr/policies' }`
  // and `{ routePrefix: '/people/policies-conduct', modulePath:
  // '/compliance/policies' }` — matched EVERY path under
  // /people/policies-conduct (assignments, publications, code, cases, the
  // bare hub), the same generic-prefix anti-pattern the /people ->
  // /compliance/workforce removal note above already warns against
  // repeating. /compliance/policies is a base compliance_essentials grant
  // every plan (including both Essentials plans) already carries, so this
  // silently re-opened the whole worker-scoped Policies & Conduct workbench
  // — including the deferred assignments/publications/code/cases children —
  // for a tenant that has never held workforce_operations. Every real
  // non-Essentials plan in BILLING_PLANS already carries workforce_operations
  // alongside compliance_essentials, so it already reaches
  // /people/policies-conduct directly and unconditionally (a literal
  // PACKAGE_MODULES.workforce_operations entry) — removing this generic pair
  // changes nothing for any of them. The one narrow, still-legitimate child
  // equivalence (governance -> /compliance/policies) is kept below.
  // Development and worker evidence are canonical People destinations backed
  // by the provider's existing workforce/performance entitlement.
  { routePrefix: '/people/development', modulePath: '/hr/performance' },
  { routePrefix: '/people/evidence', modulePath: '/hr/performance' },
  // The People overview/settings shell composes multiple separately purchased
  // workforce modules. Any existing People-family entitlement keeps the shell
  // reachable; leaf aliases above remain narrower and win first-match checks.
  //
  // Wave 5 (2026-09-21): deliberately does NOT include
  // `{ routePrefix: '/people', modulePath: '/compliance/workforce' }`. Before
  // Wave 5, `/compliance/workforce` was only ever granted together with
  // `/staff` (both are `workforce_operations` entries), so that entry was
  // redundant — `/staff` below already covered every real tenant. Once
  // compliance_essentials-only (Essentials) tenants gained `/compliance/
  // workforce` as a narrow capability (see COMPLIANCE_CAPABILITY_DEFINITIONS
  // in billing.ts), this generic PREFIX alias — `matchesPathPrefix` matches
  // every `/people/**` descendant, not just `/people` itself — silently
  // widened it into the full `/people/workers`, `/people/development`, and
  // `/people/evidence` worker-management workspace, which Essentials must
  // never reach. The narrow `/people/compliance` alias above already grants
  // exactly what Essentials should have; do not re-add a generic `/people`
  // entry pointing at `/compliance/workforce`.
  { routePrefix: '/people', modulePath: '/staff' },
  { routePrefix: '/people', modulePath: '/hr/onboarding' },
  { routePrefix: '/people', modulePath: '/hr/training' },
  { routePrefix: '/people', modulePath: '/hr/policies' },
  { routePrefix: '/people', modulePath: '/hr/performance' },
];

function matchesPathPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

export function moduleAccessPathFor(pathname: string): string {
  const alias = MODULE_ACCESS_ALIASES.find(({ routePrefix }) =>
    matchesPathPrefix(pathname, routePrefix),
  );
  if (!alias) return pathname;
  return alias.preserveSuffix
    ? `${alias.modulePath}${pathname.slice(alias.routePrefix.length)}`
    : alias.modulePath;
}

function moduleAccessCandidates(pathname: string): string[] {
  // Hiring is not the multi-module People landing shell. Generic parent
  // aliases must not widen this leaf through training, policies or staff.
  if (matchesPathPrefix(pathname, '/people/recruitment')) {
    return [pathname, '/hr/onboarding'];
  }
  return Array.from(
    new Set([
      pathname,
      ...MODULE_ACCESS_ALIASES.filter(({ routePrefix }) =>
        matchesPathPrefix(pathname, routePrefix),
      ).map((alias) =>
        alias.preserveSuffix
          ? `${alias.modulePath}${pathname.slice(alias.routePrefix.length)}`
          : alias.modulePath,
      ),
    ]),
  );
}

function modulePatternMatches(pathname: string, modulePath: string): boolean {
  const pathSegments = pathname.split('/').filter(Boolean);
  const moduleSegments = modulePath.split('/').filter(Boolean);
  if (pathSegments.length < moduleSegments.length) return false;
  return moduleSegments.every(
    (segment, index) => segment === '*' || segment === pathSegments[index],
  );
}

function modulePatternContains(parentModule: string, childModule: string): boolean {
  const parentSegments = parentModule.split('/').filter(Boolean);
  const childSegments = childModule.split('/').filter(Boolean);
  if (childSegments.length <= parentSegments.length) return false;
  return parentSegments.every(
    (segment, index) =>
      segment === '*' || childSegments[index] === '*' || segment === childSegments[index],
  );
}

function hasKnownModuleBoundary(pathname: string, enabledModule: string): boolean {
  return ALL_MODULE_BOUNDARY_PATHS.some(
    (knownPath) =>
      knownPath !== enabledModule &&
      modulePatternContains(enabledModule, knownPath) &&
      modulePatternMatches(pathname, knownPath),
  );
}

export type ModuleAccessOptions = {
  /**
   * Demo tenants keep all-access for non-payroll surfaces (sales/UAT).
   * Payroll paths still require an explicit plan module — `enabledModules = null`
   * is not a payroll entitlement.
   */
  isDemoTenant?: boolean | null;
  /**
   * A demo tenant's plan-derived modules. Demo all-access (`enabledModules = null`)
   * never covers payroll, so payroll paths are checked against these instead —
   * the same plan modules the server-side `requireModule` guard uses.
   */
  demoPlanModules?: string[] | null;
};

export function isPayrollEntitlementPath(pathname: string): boolean {
  return (
    pathname === '/payroll' ||
    pathname.startsWith('/payroll/') ||
    pathname === '/settings/payroll' ||
    pathname.startsWith('/settings/payroll/')
  );
}

export function isPlanExemptPath(pathname: string): boolean {
  if (PLAN_EXEMPT_EXACT_PATHS.has(pathname)) return true;
  return PLAN_EXEMPT_PREFIXES.some((prefix) => matchesPathPrefix(pathname, prefix));
}

export function isSubscriptionBlockedPathAccessible(pathname: string): boolean {
  if (SUBSCRIPTION_BLOCKED_EXACT_PATHS.has(pathname)) return true;
  return SUBSCRIPTION_BLOCKED_PREFIXES.some((prefix) => matchesPathPrefix(pathname, prefix));
}

function modulesForAccessCheck(
  pathname: string,
  enabledModules: string[] | null | undefined,
  options?: ModuleAccessOptions,
): string[] | null | undefined {
  if (options?.isDemoTenant === true && isPayrollEntitlementPath(pathname)) {
    return enabledModules ?? options.demoPlanModules ?? [];
  }
  return enabledModules;
}

export function hasDirectModuleAccess(
  pathname: string,
  enabledModules: string[] | null | undefined,
  options?: ModuleAccessOptions,
): boolean {
  const modules = modulesForAccessCheck(pathname, enabledModules, options);
  if (!modules) return true;
  return modules.some((modulePath) => {
    if (!modulePatternMatches(pathname, modulePath)) return false;
    if (PARENT_LAYOUT_ONLY_MODULE_PATHS.has(modulePath) && pathname !== modulePath) return false;
    return !hasKnownModuleBoundary(pathname, modulePath);
  });
}

export function hasLandingModuleAccess(
  pathname: string,
  enabledModules: string[] | null | undefined,
  options?: ModuleAccessOptions,
): boolean {
  const modules = modulesForAccessCheck(pathname, enabledModules, options);
  if (!modules) return true;
  if (!MODULE_LANDING_PATHS.has(pathname)) return false;
  return modules.some((modulePath) => matchesPathPrefix(modulePath, pathname));
}

export function isModulePathAccessible(
  pathname: string,
  enabledModules: string[] | null | undefined,
  options?: ModuleAccessOptions,
): boolean {
  // Route entitlement is defined for the pathname. Query parameters and
  // fragments select a view within that route and never change its module.
  pathname = pathname.split(/[?#]/, 1)[0] ?? pathname;
  const isComplianceTrial = isComplianceTrialModuleSet(enabledModules);
  if (isComplianceTrial && !isComplianceTrialRouteAllowed(pathname)) return false;
  if (isPlanExemptPath(pathname)) return true;
  return moduleAccessCandidates(pathname).some(
    (modulePathname) =>
      hasDirectModuleAccess(modulePathname, enabledModules, options) ||
      hasLandingModuleAccess(modulePathname, enabledModules, options),
  );
}

export function isModuleNavigationVisible(
  pathname: string,
  enabledModules: string[] | null | undefined,
  options?: ModuleAccessOptions,
): boolean {
  return isModulePathAccessible(pathname, enabledModules, options);
}
